import {
  ApplicantAnotherExperience,
  ApplicantArticle,
  ApplicantAward,
  ApplicantCommunity,
  ApplicantContest,
  ApplicantEducation,
  ApplicantMentorship,
  ApplicantOpenSource,
  ApplicantPatent,
  ApplicantPublicTalk,
  ApplicantScientificWork,
} from "@prisma/client";
import Blob from "cross-blob";
import {
  Document,
  HeadingLevel,
  ISectionOptions,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import flattenDeep from "lodash/flattenDeep";

import {
  ExperienceType,
  getExperienceSectionTitle,
} from "@/lib/experienceType";

import { docStyles } from "./common";
import { numberingStyle } from "./common/numbering";
import { DocGenerator } from "../";
import { renderProseMirrorSchemaToGoogleDoc } from "../proseMirror";
import { ApplicationFull } from "../../prisma";
import { parseProseMirrorSchema } from "../../proseMirror";

interface ExternalProfileDocArgs {
  application: ApplicationFull;
  applicantArticleList: ApplicantArticle[];
  applicantPublicTalkList: ApplicantPublicTalk[];
  applicantContestList: ApplicantContest[];
  applicantAwardList: ApplicantAward[];
  applicantPatentList: ApplicantPatent[];
  applicantOpenSourceList: ApplicantOpenSource[];
  applicantMentorshipList: ApplicantMentorship[];
  applicantTeachingList: ApplicantEducation[];
  applicantCommunityList: ApplicantCommunity[];
  applicantScientificWorkList: ApplicantScientificWork[];
  applicantAnotherExperienceList: ApplicantAnotherExperience[];
}

export const createExternalProfileDoc: DocGenerator<
  ExternalProfileDocArgs
> = async (
  t,
  {
    application,
    applicantArticleList,
    applicantPublicTalkList,
    applicantContestList,
    applicantAwardList,
    applicantPatentList,
    applicantOpenSourceList,
    applicantMentorshipList,
    applicantTeachingList,
    applicantCommunityList,
    applicantScientificWorkList,
    applicantAnotherExperienceList,
  }
) => {
  const document = new Document({
    styles: docStyles,
    numbering: numberingStyle,
    sections: [
      {
        children: flattenDeep<ISectionOptions["children"][0] | null>([
          new Paragraph({
            children: [
              new TextRun({
                text: "Applicant: ",
              }),
              new TextRun({
                text: application.applicant.fullName
                  ? application.applicant.fullName
                  : `${application.applicant.firstName} ${application.applicant.lastName}`,
              }),
            ],
            heading: HeadingLevel.HEADING_1,
          }),

          new Paragraph({
            style: "wellSpaced",
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "External Profile",
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),

          ...applicantPublicTalkList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.PublicTalks)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Conference title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to website: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Role: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.role) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any famous pros next?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.famousProsNext, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantArticleList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.Articles)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Article title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to article: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Role: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.role) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Journal Impact: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.journalImpact, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Type of publication: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.typeOfPublication, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Journal peer reviewed: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.journalPeerReviewed, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Type of mention: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.typeOfMention, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Why you mentioned: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.whyYouMentioned, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantScientificWorkList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.ScientificWorkOrPhD
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Scientific work title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Name of University or other scientific institution",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.nameOfUniversity, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantContestList
            .filter((x) => !x.isOrganizer)
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.Contests)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Event title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to website / pictures: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Role: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.role) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Your place or achievements: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.achievements, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantAwardList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.Award)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Award title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to website: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Award Description and Reputation: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.awardReputation, "-"),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantCommunityList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.CommunityDevelopment
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to the community: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantTeachingList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.TeachingAndMentorship
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to activity: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantMentorshipList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.TeachingAndMentorship
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to activity: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Teaching or mentoring term: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.terms) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "What did you teach: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.what) : "-",
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Where did you teach: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.where) : "-",
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Who did you teach: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.who) : "-",
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Number of students: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url
                        ? parseProseMirrorSchema(x.studentsNumbers)
                        : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Metrics: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantOpenSourceList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.OpenSourceContribution
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to the repo: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Any significant numbers?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.significantStats, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantPatentList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(ExperienceType.Patent)
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Patent title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link to website: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Year: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.year ? String(x.year) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),

          ...applicantAnotherExperienceList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${t(
                        getExperienceSectionTitle(
                          ExperienceType.AnotherExperience
                        )
                      )}: `,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Title: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.name ? parseProseMirrorSchema(x.name) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Link: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: x.url ? parseProseMirrorSchema(x.url) : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Period: ",
                      bold: true,
                    }),
                    new TextRun({
                      text:
                        x.fromYear && x.toYear
                          ? `${String(x.fromYear)} - ${
                              Number(x.toYear) === 2099
                                ? "Now"
                                : String(x.toYear)
                            }}`
                          : "-",
                    }),
                  ],
                }),
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Brief description: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.description, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),
        ]).filter((x): x is ISectionOptions["children"][0] => x !== null),
      },
    ],
  });

  return new Blob([await Packer.toBuffer(document)]);
};
